import { CHART_SERIES_NEUTRAL } from '@core/constants/tokens.generated';
import { conversionFor, seriesColor } from '@core/constants/role-layout.constant';
import { slotSeriesValue } from '@core/helpers/slot-series.helper';
import { Point } from '@core/models/installation.model';
import { InsightCard, InsightSeries, sourceGroup, sourceLabel } from './installation-card-set';
import { ChartOverlay } from './point-series-chart/point-series-chart.component';

export const PHASE_POWER_ROLES: ReadonlySet<string> = new Set([
  'active_power_l1',
  'active_power_l2',
  'active_power_l3',
]);

// The upper bound of the 230 V grid's +10% tolerance band.
export const VOLTAGE_LIMIT_V = 253;

const PHASE_TOTAL_COLOR_TOKEN = 11;

// What a reader takes as the total of the phases, as the device page drew it: the phases that
// have a value in the slot added up, and no total where none has.
export function phaseSum(
  points: Pick<Point, 'kind' | 'slots'>[],
  slotCount: number,
): (number | null)[] {
  return Array.from({ length: slotCount }, (_, index) => {
    const present = points
      .map(point => {
        const slot = point.slots[index];
        return slot ? slotSeriesValue(point, slot) : null;
      })
      .filter((value): value is number => value !== null);
    return present.length === 0 ? null : present.reduce((total, value) => total + value, 0);
  });
}

// One total per meter that reports two or more phases, so two meters' phases are never added up.
export function phaseTotalOverlays(
  card: InsightCard,
  slotCount: number,
  label: string,
): ChartOverlay[] {
  const groups = new Map<string, InsightSeries[]>();
  for (const series of card.series) {
    if (PHASE_POWER_ROLES.has(series.point.role)) {
      const key = sourceGroup(series.point);
      groups.set(key, [...(groups.get(key) ?? []), series]);
    }
  }
  const totals = [...groups.values()].filter(group => group.length > 1);
  return totals.map((group, index) => ({
    label: totals.length > 1 ? `${sourceLabel(group[0].point) ?? ''} ${label}`.trim() : label,
    unit: group[0].point.unit,
    values: phaseSum(
      group.map(series => ({ kind: series.point.kind, slots: series.telemetry?.slots ?? [] })),
      slotCount,
    ),
    color: seriesColor(PHASE_TOTAL_COLOR_TOKEN, index),
    borderDash: [],
    fill: true,
  }));
}

// Unlabelled, so the chart keeps it out of the legend and the tooltip. Only drawn beside a reading
// to compare it with.
export function voltageLimitOverlay(card: InsightCard, slotCount: number): ChartOverlay | null {
  if (card.key !== 'voltage') {
    return null;
  }
  const hasReading = card.series.some(({ point, telemetry }) =>
    (telemetry?.slots ?? []).some(slot => slotSeriesValue(point, slot) !== null),
  );
  if (!hasReading) {
    return null;
  }
  return {
    label: '',
    unit: 'V',
    values: Array.from({ length: slotCount }, () => VOLTAGE_LIMIT_V),
    color: CHART_SERIES_NEUTRAL,
    borderDash: [6, 4],
  };
}

export interface PeriodTotal {
  key: string;
  label: string;
  value: string;
}

// One decimal while it still carries information, none once the number runs into the hundreds
// ("12.4 kWh", "352 kWh").
export function formatPeriodTotal(value: number, unit: string): string {
  const { displayUnit, factor } = conversionFor(unit);
  const converted = value * factor;
  const decimals = Math.abs(converted) >= 100 ? 0 : 1;
  const rounded = Number(converted.toFixed(decimals));
  // A tiny negative total, and JS's -0, would otherwise read as "-0.0 kWh".
  return `${(rounded === 0 ? 0 : rounded).toFixed(decimals)} ${displayUnit}`;
}

// The grid card's title already says grid, so its totals read Import, Export and Net. A role that
// occurs twice on the card, such as two grid meters, keeps the point names to tell them apart.
const SHORT_TOTAL_LABELS: Readonly<Record<string, string>> = {
  grid_import: 'installations.insights.totals.import',
  grid_export: 'installations.insights.totals.export',
  net_grid_energy: 'installations.insights.totals.net',
};

// The API's period total of every energy series on the card, unnegated like the tooltip and the
// data table: a `down` series is drawn below the axis, but its number is what was measured.
export function periodTotals(
  card: InsightCard,
  translate?: (key: string) => string,
): PeriodTotal[] {
  const roleCount = (role: string): number =>
    card.series.filter(({ point }) => point.role === role).length;
  return card.series.flatMap(({ point, telemetry }) => {
    const quantity = telemetry?.total.quantity ?? null;
    if (point.unit !== 'Wh' || point.kind === 'average' || quantity === null) {
      return [];
    }
    const shortLabel = SHORT_TOTAL_LABELS[point.role];
    const label =
      translate && shortLabel && roleCount(point.role) === 1 ? translate(shortLabel) : point.name;
    return [{ key: point.pointOid, label, value: formatPeriodTotal(quantity, point.unit) }];
  });
}
